import { accountScopedDbName } from '#pplx/frontend/browser-storage/accountScopedIDB.js';
import { getActiveAccountId } from '#pplx/frontend/browser-storage/activeAccount.js';
import { IDB_WORKS } from '#pplx/frontend/browser-storage/indexedDB.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { clear, createStore, entries as idbEntries, setMany } from 'idb-keyval';
/** Base name of the per-account Eppo flags IndexedDB database (`eppo-sdk-{accountId}`). */
export const EPPO_DB_NAME = 'eppo-sdk';
const NO_ACCOUNT = '__no_account__';
const storePromises = new Map();
const migrationPromises = new Map();
function tryCreateStore(accountId) {
    return IDB_WORKS.then((works) => works ? createStore(accountScopedDbName(EPPO_DB_NAME, accountId), 'flags') : undefined);
}
async function getStore(accountId) {
    const cacheKey = accountId ?? NO_ACCOUNT;
    const existing = storePromises.get(cacheKey);
    if (existing) {
        return existing;
    }
    const storePromise = (async () => {
        if (accountId) {
            // Primary-account activation starts migration before publishing the active account. Waiting
            // here prevents the first account-scoped read from racing ahead of the legacy-cache copy.
            await migrationPromises.get(accountId)?.catch(() => undefined);
        }
        return tryCreateStore(accountId);
    })().catch((error) => {
        logger.debug('[Eppo IDB Store] Error creating store', error);
        return undefined;
    });
    storePromises.set(cacheKey, storePromise);
    return storePromise;
}
async function getActiveAccountStore() {
    while (true) {
        const accountId = getActiveAccountId();
        const store = await getStore(accountId);
        if (accountId === getActiveAccountId()) {
            return store;
        }
    }
}
/**
 * Moves the pre-multiaccount Eppo cache into the primary account's database.
 *
 * Register the promise synchronously so account-scoped reads can wait for it while activation
 * continues. Clearing the legacy database prevents a later primary account from inheriting the
 * previous primary's cached configuration.
 */
export function migrateEppoCacheToAccount(accountId) {
    const existing = migrationPromises.get(accountId);
    if (existing) {
        return existing;
    }
    const migration = IDB_WORKS.then(async (works) => {
        if (!works) {
            return;
        }
        const source = createStore(EPPO_DB_NAME, 'flags');
        const target = createStore(accountScopedDbName(EPPO_DB_NAME, accountId), 'flags');
        const sourceEntries = await idbEntries(source);
        if (sourceEntries.length > 0) {
            const existingKeys = new Set((await idbEntries(target)).map(([key]) => key));
            const entriesToCopy = sourceEntries.filter(([key]) => !existingKeys.has(key));
            if (entriesToCopy.length > 0) {
                await setMany(entriesToCopy, target);
            }
        }
        await clear(source);
    });
    migrationPromises.set(accountId, migration);
    return migration;
}
export class IndexedDBStore {
    isInitialized() {
        return true;
    }
    async isExpired() {
        // Refresh on every initialization when this store is passed directly to the SDK.
        return Promise.resolve(true);
    }
    async entries() {
        const store = await getActiveAccountStore();
        if (!store)
            return {};
        try {
            const allEntries = await idbEntries(store);
            return allEntries.reduce((acc, [key, value]) => {
                acc[key] = value;
                return acc;
            }, {});
        }
        catch (error) {
            logger.debug('[Eppo IDB Store] Error getting entries', error);
            return {};
        }
    }
    async setEntries(entries) {
        const store = await getActiveAccountStore();
        if (!store)
            return;
        try {
            const entriesToSet = Object.entries(entries);
            await setMany(entriesToSet, store);
        }
        catch (error) {
            logger.debug('[Eppo IDB Store] Error setting entries', error);
        }
    }
}
//# sourceMappingURL=IndexedDBStore.js.map